import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import {
  executeCampaign,
  getCampaign,
  getCampaignMetaData,
  getTemplate,
  postCampaign,
  previewTemplate,
  updateCampaign,
  updateTemplate,
  testCampaign,
  executeCampaignV2,
  getCampaignExecutionsDetail,
  getCampaignMetaDataV2
} from "../APIs/CampaignAPI";
import { useAppContext } from "./Core";
import {
  getRecipientsFieldValues,
  getCampaignStatuses,
  getNotification,
  getCampaignType,
  checkIfExecutable,
  getConfigurationFieldValues,
  addAConfiguredZoomBot,
  validateField,
  convertTemplateContentToString,
  userHasOwnerPrivileges,
  validateEmail,
  validateStringLength,
  getCampaignAttachmentsSize,
  cleanUpAttachments,
  getCampaignTypeV2,
} from "../utils/campaign";
import {
  // updateRecipientField,
  updateOrCreateNotification,
  updateNotificationConfig,
  updateMapping,
  removeNotificationAtIndex,
  getConfigIndex,
  getCurrentStepIndex,
  getStepperConfig,
  removeEmbeddedTemplates,
  getEmbeddedTemplates,
  addEmbeddedTemplates,
  updateCronTrigger,
  autoMapRecipientField,
  resetRecipients,
  updateFieldErrors,
  removeUnusedMappings,
  makeCampaignBackwardCompatibile,
  updateRecipientFieldV2,
  deleteRecipientConfigByType,
  getRecipientFields,
  updateRecipientField,
} from "../utils/campaignEdit";
import {
  getChannel,
  getFromField,
  getFromDisplayNameField,
  getSubject,
  getPriority,
  getSnippet,
  getToField,
  isNotificationTypeAllowed,
  getTrackingConfig,
  getHasExternalUsers,
  getAttachments,
} from "../utils/notification";
import {
  downloadCSV,
  getUploadedCSVFileName,
  removeUploadedCSVFileName,
} from "../utils/blobStorage";
import {
  CAMPAIGN_STATUSES,
  NOTIFICATION_CHANNELS,
  UI_DATA,
  SUBJECT_MAX_CHAR,
  SNIPPET_MAX_CHAR,
  DISPLAY_NAME_MAX_CHAR,
  CAMPAIGN_FIELDS,
  CODES,
  API_VERSION
} from "../consts/Campaign";
import { JWT } from "../consts/AuthFields";
import {
  CAMPAIGN,
  CONFIGURATIONS,
  DATA_SOURCE,
  DATA_SOURCE_CAMPAIGN_TYPES,
  EXTERNAL_USER_OPTIONS_CONFIGS,
  FOLDER,
  MAPPING,
  NOTIFICATION,
  PRIORITIES,
  RECIPIENTS,
  TEAM,
  TEMPLATE,
  TRACKING_CONFIGS,
} from "../consts/DBFields";
import {
  ROUTES,
  ERROR_MSG,
  SUCCESS_MSG,
  SNACK_TYPES,
  INFO_MSG,
  FEATURE,
  REVIEW_STEP_INDEX,
  EVENTS,
  EVENT_TYPES,
} from "../consts/Common";
import { campaignFormSchema, DEFAULT_FLOW } from "../consts/CampaignEdit";
import { CAMPAIGN_TYPES } from "../consts/Campaign";
import {
  isSignedInUserATeamOwner,
  isUserSuperAdmin,
  isUserAdmin,
  isUserCampaignOwner,
} from "../utils/user";
import {
  makeTemplateFieldName,
  removeInvalidDynamicContent,
} from "../utils/template";
import { getTemplateMustacheVariables } from "../components/template/EmailEditor/grapesjs/util";
import { useCampaignsContext } from "./CampaignsList";
import { postTeamTemplate } from "../APIs/TemplatesAPI";
import useFolder from "../hooks/useFolder";
import _ from "lodash";
import { FOLDER_LEVEL_PERMISSIONS, FOLDER_LEVEL_ROLES } from "../consts/Folders";
import { canEditCampagin } from "../utils/folderUtils";
import Configs from "../Configurations";
import { useFoldersContext } from "./FoldersList";
import { trackEvent } from "../utils/appInsight";
import { formatDate } from "../utils/common";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// Create and export the consumer, which allows state to be used by other components
export const Context = createContext();
export const CampaignState = Context.Consumer;
export const useCampaignContext = () => useContext(Context);

let originalCampaign = {}; // use this to determine whether a campaign has been edited

// Create and export the provider, which defines and controls the state
function Campaign({ children }) {
  const _isMounted = useRef(true);
  const history = useHistory();
  const { campaign_uid, folder_id} = useParams();
  const { state } = useLocation();
  const {
    user,
    selectedTeam,
    snackMessage,
    setSnackMessage,
    setSnackType,
    teamUsers,
    getMembersOfTeam,
    userTeams,
    isToggleEnabled,
    pageTitle,
    isV2_UI,
  } = useAppContext();

  const {
    setCurrentFolder,
    currentFolder,
  } = useFoldersContext();

  const {
    folder: selectedFolder,
    getFolder,
    getIsFolderOwner,
    getUserRole,
  } = useFolder();

  const { setStatusTabOverride, fetchCampaigns } = useCampaignsContext();
  const [userCanEdit, toggleUserCanEdit] = useState(false);
  const [channelAllowed, toggleChannelAllowed] = useState(false);
  const [loadingCampaign, toggleLoadingCampaign] = useState(true);
  const [campaignError, setCampaignError] = useState(null);
  const [steps, setSteps] = useState(DEFAULT_FLOW); // the edit flow stepper
  const [fieldErrors, setFieldErrors] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignType, setCampaignType] = useState(); // general or personalized
  const [isExecutable, toggleIsExecutable] = useState(false);
  const [csvFile, setCsvFile] = useState();
  const [dataSourceHeadings, setDataSourceHeadings] = useState([]);
  const [notificationIndex, setNotificationIndex] = useState(0);
  const [channel, setChannel] = useState(); // mail, zoom, teams
  const [downloadError, setDownloadError] = useState(false);
  const [isDownloading, toggleIsDownloading] = useState(false);
  const prevCampaignRef = useRef(null); // Initialize prevCampaignRef
  const [campaignLoaded, setCampaignLoaded] = useState(false);// select campaign loading

  // notification details
  const [to, setTo] = useState();
  const [cc, setCc] = useState();
  const [bcc, setBcc] = useState();
  const [from, setFrom] = useState();
  const [fromDisplayName, setFromDisplayName] = useState();
  const [subject, setSubject] = useState();
  const [snippet, setSnippet] = useState();
  const [priority, setPriority] = useState();
  const [attachments, setAttachments] = useState();
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [templatePreview, setTemplatePreview] = useState();
  // tracking configs
  const [clickTracking, setClickTracking] = useState();
  const [viewTracking, setViewTracking] = useState();
  const [advancedMetrics, setAdvancedMetrics] = useState();
  const [trackLocation, setTrackLocation] = useState();
  const [savingCampaign, toggleSavingCampaign] = useState(false);
  const [needToSaveTemplate, setNeedToSaveTemplate] = useState(false);
  const [campaignExecutions, setCampaignExecutions] = useState([]);
  const [campaignLastSavedTime, setCampaignLastSavedTime] = useState("");

  const trackingMetricSetter = {
    [TRACKING_CONFIGS.VIEW]: setViewTracking,
    [TRACKING_CONFIGS.CLICK]: setClickTracking,
    [TRACKING_CONFIGS.ADVANCED_METRICS]: setAdvancedMetrics,
    [TRACKING_CONFIGS.TRACK_LOCATION]: setTrackLocation,
  };

  // external users options config
  const [hasExternalUsers, setHasExternalUsers] = useState();

  //React hook form for campaign details
  // Initialize form with data from campaign context
  const formMethods = useForm({
    defaultValues: {},
    mode: 'onChange',
    resolver: yupResolver(campaignFormSchema)
  });
  const { watch, reset, setValue } = formMethods;
  // Watch form values for changes
  const formValues = watch();
  const [stepValidationStatus, setStepValidationStatus] = useState({});
  const [campaignEditSteps, setCampaignEditSteps] = useState([]);
  const [dataSourceHeadingsV2, setDataSourceHeadingsV2] = useState({});

  useEffect(() => {
    if (userTeams?.length) {
      selectCampaignV2();
      return () => {
        _isMounted.current = false;
      };
    }
  }, [campaign_uid, userTeams]);

  useEffect(() => {
    if (campaignError) {
      setSnackMessage(campaignError);
      setSnackType(SNACK_TYPES.ERROR);
    }
  }, [campaignError]);

  useEffect(() => {
    if (!snackMessage) {
      setCampaignError(null);
    }
  }, [snackMessage]);

  useEffect(() => {
    // check executable status when required fields are edited
    if (selectedCampaign) {
      toggleIsExecutable(checkIfExecutable(selectedCampaign, needToSaveTemplate, isToggleEnabled(FEATURE.EXTERNAL_USERS_CHECK), campaignType));
    }
  }, [
    to,
    from,
    fromDisplayName,
    subject,
    selectedTemplate,
    selectedCampaign?.[CAMPAIGN.METADATA]?.[CAMPAIGN.METADATA_HEADINGS],
    needToSaveTemplate,
    hasExternalUsers
  ]);

  // // look for an existing csv upload if campaign type is personalized
  useEffect(() => {
    getExistingCsv(selectedCampaign);
  }, [campaignType]);

  // set prevCampaignRef
  useEffect(() => {
    
    // only update if specified
    if (selectedCampaign && selectedCampaign.updatePrevRef !== false) {
      prevCampaignRef.current = _.cloneDeep(selectedCampaign);
    }
  }, [selectedCampaign]);

  const createCampaignBase = async (campaignData) => {
    setCampaignError(null);
    try {
        const response = await postCampaign(campaignData);
        return {
          success: true,
          response,
        };
    } catch (error) {
        let message = ERROR_MSG.POST_CAMPAIGN;
        if (error.response?.data?.code === CODES.CAMPAIGN_NAME_TAKEN) {
            message = ERROR_MSG.CAMPAIGN_NAME_TAKEN;
        } else if (error.response?.data?.message) {
          message = error.response.data.message;
        }
        setCampaignError(message);
        return {
          success: false,
          error
        };
    }
  };

  const createCampaign = async (name, categoryId, isSystemNotifications) => {
    const teamId = selectedTeam[TEAM.ID];
    const userUpn = user[JWT.UPN];    
    if (name && categoryId && teamId && userUpn && folder_id) {
      const campaignData = {
        [CAMPAIGN.NAME]: name.trim(),
        [CAMPAIGN.CATEGORY_ID]: Number(categoryId),
        [CAMPAIGN.FOLDER_ID]: folder_id,
        [CAMPAIGN.IS_DRAFT]: true,
        [CAMPAIGN.TEAM_ID]: teamId,
        [CAMPAIGN.OWNER]: userUpn,
        [CAMPAIGN.NOTIFICATIONS]: [{}],
        [CAMPAIGN.IS_SYSTEM_NOTIFICATIONS]: isSystemNotifications,
        general_data_source_type: null,
      };
      const result = await createCampaignBase(campaignData);
      if(!result.success) { //Return error if no campaignId present in the response
        return result.error;
      }
      const createdId = result.response?.campaignId;
      history.push(`/${ROUTES.CAMPAIGN}/${createdId}`, { from: "/" });
    }
  };

  const createCampaignV2 = async (campaignObj) => {
    setCampaignError(null);
    const teamId = selectedTeam[TEAM.ID];
    const userUpn = user[JWT.UPN];
    const folderId = currentFolder[FOLDER.FOLDER_ID];
    if (campaignObj[CAMPAIGN.NAME] && teamId && userUpn && folderId) {
      const campaignData = {
        ...campaignObj,
        [CAMPAIGN.FOLDER_ID]: folderId,
        [CAMPAIGN.IS_DRAFT]: true,
        [CAMPAIGN.TEAM_ID]: teamId,
        [CAMPAIGN.OWNER]: userUpn,
      };
      const result = await createCampaignBase(campaignData);
      if(!result.success) { //Return error if no campaignId present in the response
        return result.error;
      }
      const createdId = result.response?.campaignId;
      history.push(`/${ROUTES.CAMPAIGN}/${createdId}/edit`, { from: "/" });
    }
  }

  // fetches a campaign by id and sets campaign state with the campaign's values
  const selectCampaign = async () => {
    setCampaignError(null);
    let campaignToSelect = {};
    let channel;
    let campaignType;
    if (campaign_uid) {
      try {
        campaignToSelect = await getCampaign(campaign_uid);
        campaignToSelect = makeCampaignBackwardCompatibile(campaignToSelect, notificationIndex);
        originalCampaign = _.cloneDeep(campaignToSelect);
      } catch (err) {
        if (_isMounted.current) {
          originalCampaign = null;
          setSelectedCampaign(null);
          toggleLoadingCampaign(false);
          trackEvent(EVENTS.ERRORS.GENERIC_ERROR, {errorMsg: err?.message, pageTitle: pageTitle}, EVENT_TYPES.CREATE_MESSAGE_ERRORS.GET_CAMPAIGN_ERROR)
          return;
        }
      }
      
      if (_isMounted.current) {
        const teamId = campaignToSelect?.[CAMPAIGN.TEAM_ID];
        let teamMembers = teamUsers?.hasOwnProperty(teamId)
          ? teamUsers[teamId]
          : await getMembersOfTeam(teamId);
        const folderId = campaignToSelect?.[CAMPAIGN.FOLDER_ID];
        const folder = await getFolder(folderId);
        setCurrentFolder(folder);
        const isFolderOwner = getIsFolderOwner(user, folder[FOLDER.PERMISSIONS]);
        const folderUserRole = getUserRole(user, folder[FOLDER.PERMISSIONS]);
        const signedInUserIsOwner = isSignedInUserATeamOwner(user, teamMembers);
        const folderType = folder[FOLDER.PERMISSION_TYPE];
        
        let isOwner = canEditCampagin(user, signedInUserIsOwner, campaignToSelect, isFolderOwner, folderUserRole, folderType);

        // if no notification exists yet, create it
        campaignToSelect = updateOrCreateNotification(
          campaignToSelect,
          notificationIndex
        );
        
        channel = getChannel(
          getNotification(campaignToSelect, notificationIndex)
        );
        let campaignTeam = selectedTeam;
        if (teamId !== selectedTeam?.[TEAM.ID]) {
          campaignTeam =
            userTeams?.find((team) => team[TEAM.ID] === teamId) || selectedTeam;
        }
        const typeAllowed = isNotificationTypeAllowed(channel, campaignTeam);
        toggleUserCanEdit(isOwner && typeAllowed);
        toggleChannelAllowed(typeAllowed);
        if (
          channel === NOTIFICATION_CHANNELS.teams.value
        ) {
          campaignType = CAMPAIGN_TYPES.personalized.value;
        } else {
          campaignType = getCampaignType(campaignToSelect, notificationIndex);
        }
        
        // configure the stepper
        const stepper = getStepperConfig(campaignToSelect, channel, campaignType);
        setSteps(stepper);
        const landingStep = getCurrentStepIndex(campaignToSelect, stepper);
        if (isOwner && typeAllowed) {
          setCurrentStep(
            state?.duplicate && landingStep !== stepper?.length - 1
              ? 0
              : landingStep
          );
        } else {
          setCurrentStep(stepper?.length - 1);
        }
      }
    }
    
    setSelectedCampaign(campaignToSelect);
    
    setChannel(channel);
    setCampaignType(campaignType);
    // Set campaign last saved from selectedCampaign
    handleCampaignLastSavedTime(campaignToSelect);

    await setNotificationDetails(campaignToSelect, channel, campaignType);
    if (_isMounted.current) {
      // if campaign has been sent, redirect to the overview page
      const statuses = getCampaignStatuses(campaignToSelect) || [];
      if (!state?.isResend && statuses.includes(CAMPAIGN_STATUSES.SENT.value)) {
        toggleLoadingCampaign(false);
        history.replace({
          pathname: `/${ROUTES.CAMPAIGN}/${campaign_uid}`,
          state: { sent: true, from: history.location?.state?.from },
        });
        await loadCampaignExecutions();
        return;
      }
      toggleIsExecutable(checkIfExecutable(campaignToSelect, needToSaveTemplate, isToggleEnabled(FEATURE.EXTERNAL_USERS_CHECK), campaignType));
      toggleLoadingCampaign(false);
    }
  };

  const selectCampaignV2 = async () => {
    setCampaignError(null);
    let campaignToSelect = {};
    let channel;
    let campaignType;
    let dataSourceHeadingsV2 = {};
    if (campaign_uid) {
      try {
        // 1. Fetch campaign
        campaignToSelect = await getCampaign(campaign_uid);
        campaignToSelect = makeCampaignBackwardCompatibile(campaignToSelect, notificationIndex);
        originalCampaign = _.cloneDeep(campaignToSelect);
        
        // 2. Set channel FIRST (required by updateDataSourceHeadings)
        channel = getChannel(getNotification(campaignToSelect, notificationIndex));  
        setChannel(channel);

        // 3. Set campaign type FIRST
        if (channel === NOTIFICATION_CHANNELS.teams.value) {
          campaignType = CAMPAIGN_TYPES.personalized.value;
        } else {
          campaignType = getCampaignType(campaignToSelect, notificationIndex);
        }
        setCampaignType(campaignType);

        // 4. AWAIT CSV file load before updating data source headings
        const csvFile = await getCSVFile(campaignToSelect);

        // 5. Now update data source headings with all dependencies ready
        //Load data source headers
        const headings = await updateDataSourceHeadings(campaignToSelect, false, channel);
        setDataSourceHeadings(headings);

      } catch (err) {
        if(err?.response?.status === 404) {
          setCampaignError("Sorry, the campaign can't be found");
        }
        if (_isMounted.current) {
          originalCampaign = null;
          setSelectedCampaign(null);
          toggleLoadingCampaign(false);
          trackEvent(EVENTS.ERRORS.GENERIC_ERROR, {errorMsg: err?.message, pageTitle: pageTitle}, EVENT_TYPES.CREATE_MESSAGE_ERRORS.GET_CAMPAIGN_ERROR)
          return;
        }
      }

  
      // if (_isMounted.current) {
        const teamId = campaignToSelect?.[CAMPAIGN.TEAM_ID];
        let teamMembers = teamUsers?.hasOwnProperty(teamId)
          ? teamUsers[teamId]
          : await getMembersOfTeam(teamId);
        const folderId = campaignToSelect?.[CAMPAIGN.FOLDER_ID];
        const folder = await getFolder(folderId);
        setCurrentFolder(folder);
        const isFolderOwner = getIsFolderOwner(user, folder[FOLDER.PERMISSIONS]);
        const folderUserRole = getUserRole(user, folder[FOLDER.PERMISSIONS]);
        const signedInUserIsOwner = isSignedInUserATeamOwner(user, teamMembers);
        const folderType = folder[FOLDER.PERMISSION_TYPE];
        
        let isOwner = canEditCampagin(user, signedInUserIsOwner, campaignToSelect, isFolderOwner, folderUserRole, folderType);

        // if no notification exists yet, create it
        campaignToSelect = updateOrCreateNotification(
          campaignToSelect,
          notificationIndex
        );
        
        // channel = getChannel(
        //   getNotification(campaignToSelect, notificationIndex)
        // );
        let campaignTeam = selectedTeam;
        if (teamId !== selectedTeam?.[TEAM.ID]) {
          campaignTeam =
            userTeams?.find((team) => team[TEAM.ID] === teamId) || selectedTeam;
        }
        const typeAllowed = isNotificationTypeAllowed(channel, campaignTeam);
        toggleUserCanEdit(isOwner && typeAllowed);
        toggleChannelAllowed(typeAllowed);

    }
    
    setSelectedCampaign(campaignToSelect);


    // Set campaign last saved from selectedCampaign
    handleCampaignLastSavedTime(campaignToSelect);

    // await setNotificationDetailsV2(campaignToSelect, channel, campaignType);
    await setNotificationDetails(campaignToSelect, channel, campaignType);
    setTo(getRecipientFields(campaignToSelect, notificationIndex, RECIPIENTS.TO));
    setCc(getRecipientFields(campaignToSelect, notificationIndex, RECIPIENTS.CC));
    setBcc(getRecipientFields(campaignToSelect, notificationIndex, RECIPIENTS.BCC));

    setCampaignLoaded(true);
    toggleLoadingCampaign(false);
  }

  const saveCampaign = async (campaign, showSuccessMessage = true) => {
    
    const canSaveCampaign = await editCampaignPermissionCheck(campaign);
    
    if(!canSaveCampaign) return;
    setCampaignError(null);
    toggleSavingCampaign(true);
    try {
      let campaignToSave = _.cloneDeep(campaign || selectedCampaign);
      if (
        channel &&
        channel !== NOTIFICATION_CHANNELS.mail.value &&
        campaignType &&
        campaignType !== CAMPAIGN_TYPES.personalized.value
      ) {
        campaignToSave[CAMPAIGN.DATA_SOURCE] = {
          [DATA_SOURCE.TYPE]:
            CAMPAIGN_TYPES.personalized.dataSourceTypes[
              CAMPAIGN_TYPES.personalized.defaultType
            ].value,
        };
      }
      const embeddedTemplates = getEmbeddedTemplates(campaignToSave);
      campaignToSave = await updateVariableMapping(campaignToSave);
      const response = await updateCampaign(campaignToSave);
      if (isToggleEnabled(FEATURE.EMAIL_ATTACHMENTS)) {
        const notification = getNotification(campaignToSave, notificationIndex);
        const _attachments = getAttachments(notification);
        cleanUpAttachments(campaignToSave, _attachments);
      }
      if (showSuccessMessage) {
        setSnackMessage(SUCCESS_MSG.PUT_CAMPAIGN);
        setSnackType(SNACK_TYPES.SUCCESS);
      }
      campaignToSave = await addEmbeddedTemplates(
        campaignToSave,
        embeddedTemplates
      );
      originalCampaign = _.cloneDeep(campaignToSave);
      setSelectedCampaign(campaignToSave);
  
      setChannel(getChannel(getNotification(campaignToSave, notificationIndex)));
      // setCampaignType(getCampaignTypeV2(campaignToSave));
      setCampaignType(getCampaignType(campaignToSave));

      // Set camapign last saved from response
      const lastSaved = response.lastSaved;
      handleCampaignLastSavedTime(campaignToSave, lastSaved);
      toggleSavingCampaign(false);
    } catch (error) {
      let message = ERROR_MSG.PUT_CAMPAIGN;
      if (error.response?.data?.code === CODES.CAMPAIGN_NAME_TAKEN) {
        message = ERROR_MSG.CAMPAIGN_NAME_TAKEN;
      }
      setCampaignError(message);
      toggleSavingCampaign(false);
      trackEvent(EVENTS.ERRORS.GENERIC_ERROR, {errorMsg: message, pageTitle: pageTitle}, EVENT_TYPES.CREATE_MESSAGE_ERRORS.SAVE_CAMPAIGN_ERROR)
      return error;
    }
  };

    // debounced save campaign function
  const debouncedCampaignSaveRef = useRef();
  useEffect(() => {
    debouncedCampaignSaveRef.current = _.debounce(saveCampaign, 2000);
  }, [currentFolder, teamUsers]);

  const loadCampaignExecutions = async () => {
    if(campaign_uid) {
      const rawCampaignExecutions = await getCampaignExecutionsDetail(campaign_uid, { onlyCampaignExecutions: true });
      setCampaignExecutions(rawCampaignExecutions || []);
    }
  }

  const updateVariableMapping = async (campaign, isCampaignSend = false) => {
    console.log("updateVariableMapping - campaign", campaign);
    
    let dataSourceHeadingsCopy = [...dataSourceHeadings];
    let campaignToSave = _.cloneDeep(campaign);  
    let metaDataHeadings = dataSourceHeadingsCopy.map(heading => heading.value);
    if(_.isEmpty(metaDataHeadings)) {
      metaDataHeadings = undefined;
    }
    campaignToSave[CAMPAIGN.IS_DRAFT] = !checkIfExecutable(
      campaignToSave,
      needToSaveTemplate,
      isToggleEnabled(FEATURE.EXTERNAL_USERS_CHECK),
      campaignType,
      metaDataHeadings,
      (errorMsg) => {
        if(!isCampaignSend) return;
        setSnackMessage(errorMsg);
        setSnackType(SNACK_TYPES.ERROR);
      }
    );
    console.log({campaignType});
    console.log([to, cc, bcc]);
    
    if (
      campaignType === CAMPAIGN_TYPES.personalized.value ||
      campaign?.general_data_source_type === "csv"
    ) {
      [to, cc, bcc].forEach((recipientValue) => {
        campaignToSave = autoMapRecipientField(
          campaignToSave,
          notificationIndex,
          recipientValue
        );
      });
      console.log("after autoMapping", campaignToSave);
      
      let variables = getTemplateMustacheVariables(
        selectedTemplate?.[TEMPLATE.CONTENT]
      );
      Object.keys(variables)?.forEach((templateFieldName) => {
        const cip = variables[templateFieldName].cip;
        campaignToSave = updateMapping(
          campaignToSave,
          notificationIndex,
          templateFieldName,
          templateFieldName,
          cip ? MAPPING.CIP : null,
          cip ? to : variables[templateFieldName].srcname
        );
      });

      // This is for CSV campaigns where we need to re-make data on review step
      if (dataSourceHeadings.length === 0) {
        if( (currentStep === REVIEW_STEP_INDEX.CHANNEL_MAIL && channel === NOTIFICATION_CHANNELS.mail.value) || 
            (currentStep === REVIEW_STEP_INDEX.CHANNEL_TEAMS && channel === NOTIFICATION_CHANNELS.teams.value)
        ) {
          console.log("data source heading updated - campaignContext");
          const headings = await updateDataSourceHeadings(campaignToSave);
          dataSourceHeadingsCopy = [...headings];
        }
      }
    }
    if (fromDisplayName && !isToggleEnabled(FEATURE.USE_FROM_DISPLAY_NAME)) {
      campaignToSave = updateNotificationConfig(
        campaignToSave,
        notificationIndex,
        CONFIGURATIONS.FROM_DISPLAY_NAME,
        null
      );
      setFromDisplayName(null);
    }
    campaignToSave = await removeEmbeddedTemplates(campaignToSave);
    campaignToSave = await removeUnusedMappings(
      campaignToSave,
      dataSourceHeadingsCopy
    );
    console.log("updateVariableMapping - campaignToSave", campaignToSave);
    
    return campaignToSave;
  };

  const sendCampaign = async (scheduler) => {
    let error;
    setCampaignError(null);
    let campaignToSave = await updateVariableMapping(selectedCampaign, true);
    try {
      if (scheduler) {
        campaignToSave = updateCronTrigger(campaignToSave, scheduler);
      }
      await updateCampaign(campaignToSave);
      if (!scheduler) {
        if (campaignToSave[CAMPAIGN.IS_DRAFT]) {
          // Validation failed - snack error already shown, do not attempt execution
          return;
        }
        await executeCampaignV2(campaignToSave[CAMPAIGN.UID]);
        campaignToSave[CAMPAIGN.DATE_LAST_EXECUTED] = new Date();
      }
      let campaignConfigs = getConfigurationFieldValues(selectedCampaign, notificationIndex)
      trackEvent(EVENTS.USER_INTERACTION, {
        campaignId: selectedCampaign?.uid,
        campaignType: campaignType,
        trackingConfigs:{
          trackClick:campaignConfigs?.trackClick,
          trackOpens:campaignConfigs?.trackView,
          trackLocations:campaignConfigs?.trackClickLocations,
          advancedTracking: campaignConfigs?.trackViewAdvancedMetrics
        }
      },
      EVENT_TYPES.USER_INTERACTION.SEND_CAMPAIGN)
      trackEvent(
        EVENTS.TEAM.TEAM_ACTIVITY,
        {
          teamName: selectedTeam[TEAM.NAME],
          teamId: selectedTeam[TEAM.ID],
          folderId: currentFolder[FOLDER.FOLDER_ID],
          folderName: currentFolder[FOLDER.NAME],
        },
        EVENT_TYPES.TEAM_ACTIVITY.SEND_MESSAGE,
      );
      const updatedCampaign = await getCampaign(campaignToSave[CAMPAIGN.UID]);
      campaignToSave = { ...campaignToSave, ...updatedCampaign };
      error = false;
    } catch (err) {
      error = true;
      trackEvent(EVENTS.ERRORS.GENERIC_ERROR, {errorMsg: err?.message, pageTitle: pageTitle}, EVENT_TYPES.CREATE_MESSAGE_ERRORS.SEND_CAMPAIGN_ERROR)
    } finally {
      setSelectedCampaign(campaignToSave);
    }
    return error;
  };

  const scheduleCampaign = async (cronExp) => {
    const error = await sendCampaign(cronExp);
    if (error) {
      setSnackMessage(ERROR_MSG.SCHEDULE_CAMPAIGN);
      setSnackType(SNACK_TYPES.ERROR);
      trackEvent(EVENTS.ERRORS.GENERIC_ERROR, {errorMsg: ERROR_MSG.SCHEDULE_CAMPAIGN, pageTitle: pageTitle}, EVENT_TYPES.CREATE_MESSAGE_ERRORS.SCHEDULE_CAMPAIGN_ERROR)
    } else {
      setSnackType(SNACK_TYPES.SUCCESS);
      setSnackMessage(SUCCESS_MSG.SCHEDULE_CAMPAIGN);
    }
    return error;
  };

  const cancelCampaignSchedule = async (campaign, isOnlyCancel = false) => {
    if (campaign) {
      try {
        const campaignToSave = updateCronTrigger(campaign, null);
        await updateCampaign(campaignToSave);
        setSnackMessage(INFO_MSG.CANCEL_SCHEDULE);
        setSnackType(SNACK_TYPES.INFO);
        originalCampaign = _.cloneDeep(campaignToSave);
        let sent = campaignToSave?.[CAMPAIGN.DATE_LAST_EXECUTED] ? true : false;
        if(!isOnlyCancel){
          history.push({
            pathname: `/${ROUTES.CAMPAIGN}/${campaignToSave[CAMPAIGN.UID]}/edit`,
            state: { isResend: sent, from: "/" },
          });
      }
      } catch (err) {
        setSnackMessage(ERROR_MSG.CANCEL_SCHEDULE);
        setSnackType(SNACK_TYPES.ERROR);
      }
    }
    return;
  };

  const scheduleCampaignV2 = (cronExp) => {
    const campaignToSave = updateCronTrigger(selectedCampaign, cronExp);
    setSelectedCampaign(campaignToSave);
  }

  const cancelCampaignScheduleV2 = () => {
    scheduleCampaignV2(null);
  }

  const sendCampaignTest = async (recipients = [], variables = {}) => {
    setCampaignError(null);
    try {
      if (!selectedCampaign) {
        throw new Error();
      }
      let bodyContent;
      await saveCampaign(selectedCampaign, false);
      const template = await getTemplate(
        selectedCampaign[CAMPAIGN.UID],
        channel,
        channel === NOTIFICATION_CHANNELS.mail.value ? true : false
      );
      if (!template) {
        throw new Error();
      }
      if (channel === NOTIFICATION_CHANNELS.mail.value) {
        bodyContent = template?.[TEMPLATE.HTML];
        if (!bodyContent) {
          throw new Error("template html is empty");
        }
      } else {
        bodyContent = template?.[TEMPLATE.CONTENT];
        // needed for teams notification type
        if (bodyContent && typeof bodyContent === "string") {
          const parsedContent = JSON.parse(bodyContent);
          if (parsedContent) {
            bodyContent = parsedContent;
          }
        }
      }
      variables[UI_DATA.SNIPPET_TEXT] = snippet ?? "";
      let params = {
        to: recipients,
        [channel === NOTIFICATION_CHANNELS.mail.value ? "body" : "content"]:
          bodyContent,
        variables: variables,
        istestmessage: true
      };
      if (subject) {
        params.subject = subject;
      }
      let fromAddress = from ? from : user[JWT.UPN];
      if (fromDisplayName && isToggleEnabled(FEATURE.USE_FROM_DISPLAY_NAME)) {
        params.from = `${fromDisplayName} <${fromAddress}>`;
      } else {
        params.from = fromAddress;
      }

      // including attachments in the test notification
      const notification = getNotification(selectedCampaign, notificationIndex);
      const _attachments = getAttachments(notification);
      if (_attachments) {
        params.attachments = _attachments;
      }
      await testCampaign(selectedCampaign[CAMPAIGN.UID], params);
      try {
        const updatedCampaign = await getCampaign(selectedCampaign[CAMPAIGN.UID]);
        setSelectedCampaign({ ...selectedCampaign, ...updatedCampaign });
        setSnackType(SNACK_TYPES.SUCCESS);
        setSnackMessage(SUCCESS_MSG.TEST_CAMPAIGN);
      }
      catch(err) {
        setSnackType(SNACK_TYPES.ERROR);
      } 
    } catch (err) {
      setCampaignError(ERROR_MSG.TEST_CAMPAIGN);
      trackEvent(EVENTS.ERRORS.GENERIC_ERROR, {errorMsg: ERROR_MSG.TEST_CAMPAIGN, pageTitle: pageTitle}, EVENT_TYPES.CREATE_MESSAGE_ERRORS.SEND_TEST_CAMPAIGN_ERROR)
    }
  };

  const saveTemplate = async ({
    content = "",
    isNewTemplate,
    showMessage = true,
  }) => {
    setCampaignError(null);
    try {
      await updateTemplate(
        {
          [TEMPLATE.CONTENT]:
            channel === NOTIFICATION_CHANNELS.mail.value
              ? content
              : JSON.parse(content),
        },
        selectedCampaign[CAMPAIGN.UID],
        channel
      );
      getTemplatePreview(selectedCampaign, content);
      const updatedTemplate = await getTemplate(
        selectedCampaign[CAMPAIGN.UID],
        channel
      );
      let notification =
        getNotification(selectedCampaign, notificationIndex) || {};
      notification[NOTIFICATION.TEMPLATE] = updatedTemplate;
      const updatedCampaign = selectedCampaign;
      updatedCampaign[CAMPAIGN.NOTIFICATIONS][notificationIndex] = notification;
      setSelectedCampaign(updatedCampaign);
      setSelectedTemplate(convertTemplateContentToString(updatedTemplate));
      if (!isNewTemplate && showMessage) {
        setSnackMessage(SUCCESS_MSG.PUT_TEMPLATE);
        setSnackType(SNACK_TYPES.SUCCESS);
      }
      return {
        success: true,
      }
    } catch (err) {
      setCampaignError(ERROR_MSG.PUT_TEMPLATE);
      trackEvent(EVENTS.ERRORS.GENERIC_ERROR, {errorMsg: ERROR_MSG.PUT_TEMPLATE, pageTitle: pageTitle}, EVENT_TYPES.CREATE_MESSAGE_ERRORS.SAVE_TEMPLATE_ERROR)
      return {
        success: false,
        message: ERROR_MSG.PUT_TEMPLATE
      }
    }
  };

  //OLD v1
  const setNotificationDetails = async (campaign, channel, campaignType) => {
    let recipients = {};
    let configurations = {};
    let template;
    
    if (campaign) {
      recipients = getRecipientsFieldValues(
        campaign,
        notificationIndex,
        csvFile
      );
      configurations = getConfigurationFieldValues(campaign, notificationIndex);
      if (channel) {
        try {
          template = await getTemplate(campaign[CAMPAIGN.UID], channel);
        } catch (err) {
          // no template created yet
        }
      }
      getTemplatePreview(campaign, template?.[TEMPLATE.CONTENT]);
    }
    // if (_isMounted.current) {
      let errors = [];
      setTo(recipients[RECIPIENTS.TO]);
      setCc(recipients[RECIPIENTS.CC]);
      setBcc(recipients[RECIPIENTS.BCC]);
      if (
        campaignType === CAMPAIGN_TYPES.general.value &&
        campaign?.general_data_source_type !== "csv"
      ) {
        if (
          recipients[RECIPIENTS.TO] &&
          !validateEmail(recipients[RECIPIENTS.TO])
        ) {
          errors.push(RECIPIENTS.TO);
        }
        if (
          recipients[RECIPIENTS.CC] &&
          !validateEmail(recipients[RECIPIENTS.CC])
        ) {
          errors.push(RECIPIENTS.CC);
        }
        if (
          recipients[RECIPIENTS.BCC] &&
          !validateEmail(recipients[RECIPIENTS.BCC])
        ) {
          errors.push(RECIPIENTS.BCC);
        }
      }
  
      setFrom(configurations[CONFIGURATIONS.FROM]);
      setFromDisplayName(configurations[CONFIGURATIONS.FROM_DISPLAY_NAME]);
      if (isToggleEnabled(FEATURE.USE_FROM_DISPLAY_NAME)) {
        if (
          !validateStringLength(
            configurations[CONFIGURATIONS.FROM_DISPLAY_NAME],
            DISPLAY_NAME_MAX_CHAR
          )
        ) {
          errors.push(CONFIGURATIONS.FROM_DISPLAY_NAME);
        }
      }
      setSubject(configurations[CONFIGURATIONS.SUBJECT]);
      if (
        !validateStringLength(
          configurations[CONFIGURATIONS.SUBJECT],
          SUBJECT_MAX_CHAR
        )
      ) {
        errors.push(CONFIGURATIONS.SUBJECT);
      }
      setPriority(configurations[CONFIGURATIONS.PRIORITY]);
      setSnippet(configurations[UI_DATA.SNIPPET_TEXT]);
      if (
        !validateStringLength(
          configurations[UI_DATA.SNIPPET_TEXT],
          SNIPPET_MAX_CHAR
        )
      ) {
        errors.push(UI_DATA.SNIPPET_TEXT);
      }
      setAttachments(configurations[CONFIGURATIONS.ATTACHMENTS]);
      setClickTracking(configurations[TRACKING_CONFIGS.CLICK]);
      setViewTracking(configurations[TRACKING_CONFIGS.VIEW]);
      setAdvancedMetrics(configurations[TRACKING_CONFIGS.ADVANCED_METRICS]);
      setTrackLocation(configurations[TRACKING_CONFIGS.TRACK_LOCATION]);
      setHasExternalUsers(configurations[EXTERNAL_USER_OPTIONS_CONFIGS.INCLUDE_UNSUBSCRIBE_LINK]);
      setSelectedTemplate(
        template ? convertTemplateContentToString(template) : null
      );
      setFieldErrors(errors);
    // }
  };

  //NEW V2
  const setNotificationDetailsV2 = async (campaign, channel, campaignType) => {
    let recipients = {};
    let configurations = {};
    let template;
    
    if (campaign) {
      // recipients = getRecipientsFieldValues(
      //   campaign,
      //   notificationIndex,
      //   csvFile
      // );
      configurations = getConfigurationFieldValues(campaign, notificationIndex);
      if (channel) {
        try {
          template = await getTemplate(campaign[CAMPAIGN.UID], channel);
        } catch (err) {
          // no template created yet
        }
      }
      getTemplatePreview(campaign, template?.[TEMPLATE.CONTENT]);
    }
    // if (_isMounted.current) {
      let errors = [];
      const { to, cc, bcc } = getRecipientConfigStatesFromCampaign(campaign, notificationIndex);
      setRecipientConfigToStates({ to, cc, bcc });
      //TO DO VALIDATION OF EMAIL FIELDS
      // if (
      //   campaignType === CAMPAIGN_TYPES.general.value &&
      //   campaign?.general_data_source_type !== "csv"
      // ) {
      //   if (
      //     recipients[RECIPIENTS.TO] &&
      //     !validateEmail(recipients[RECIPIENTS.TO])
      //   ) {
      //     errors.push(RECIPIENTS.TO);
      //   }
      //   if (
      //     recipients[RECIPIENTS.CC] &&
      //     !validateEmail(recipients[RECIPIENTS.CC])
      //   ) {
      //     errors.push(RECIPIENTS.CC);
      //   }
      //   if (
      //     recipients[RECIPIENTS.BCC] &&
      //     !validateEmail(recipients[RECIPIENTS.BCC])
      //   ) {
      //     errors.push(RECIPIENTS.BCC);
      //   }
      // }
  
      setFrom(configurations[CONFIGURATIONS.FROM]);
      setFromDisplayName(configurations[CONFIGURATIONS.FROM_DISPLAY_NAME]);
      if (isToggleEnabled(FEATURE.USE_FROM_DISPLAY_NAME)) {
        if (
          !validateStringLength(
            configurations[CONFIGURATIONS.FROM_DISPLAY_NAME],
            DISPLAY_NAME_MAX_CHAR
          )
        ) {
          errors.push(CONFIGURATIONS.FROM_DISPLAY_NAME);
        }
      }
      setSubject(configurations[CONFIGURATIONS.SUBJECT]);
      if (
        !validateStringLength(
          configurations[CONFIGURATIONS.SUBJECT],
          SUBJECT_MAX_CHAR
        )
      ) {
        errors.push(CONFIGURATIONS.SUBJECT);
      }
      setPriority(configurations[CONFIGURATIONS.PRIORITY]);
      setSnippet(configurations[UI_DATA.SNIPPET_TEXT]);
      if (
        !validateStringLength(
          configurations[UI_DATA.SNIPPET_TEXT],
          SNIPPET_MAX_CHAR
        )
      ) {
        errors.push(UI_DATA.SNIPPET_TEXT);
      }
      setAttachments(configurations[CONFIGURATIONS.ATTACHMENTS]);
      setClickTracking(configurations[TRACKING_CONFIGS.CLICK]);
      setViewTracking(configurations[TRACKING_CONFIGS.VIEW]);
      setAdvancedMetrics(configurations[TRACKING_CONFIGS.ADVANCED_METRICS]);
      setTrackLocation(configurations[TRACKING_CONFIGS.TRACK_LOCATION]);
      setHasExternalUsers(configurations[EXTERNAL_USER_OPTIONS_CONFIGS.INCLUDE_UNSUBSCRIBE_LINK]);
      setSelectedTemplate(
        template ? convertTemplateContentToString(template) : null
      );
      setFieldErrors(errors);
    // }
  };

  /**
   * Get the recipient configuration states for a specific campaign and notification index.
   * @param {*} campaign 
   * @param {*} notificationIndex 
   * @returns  {Object} An object containing the recipient states for 'to', 'cc', and 'bcc'.
   */
  const getRecipientConfigStatesFromCampaign = (campaign, notificationIndex) => {
    const to = getRecipientFields(campaign, notificationIndex, RECIPIENTS.TO);
    const cc = getRecipientFields(campaign, notificationIndex, RECIPIENTS.CC);
    const bcc = getRecipientFields(campaign, notificationIndex, RECIPIENTS.BCC);
    return { to, cc, bcc };
  }

  const setRecipientConfigToStates = (recipientConfig) => {
    setTo(recipientConfig[RECIPIENTS.TO]);
    setCc(recipientConfig[RECIPIENTS.CC]);
    setBcc(recipientConfig[RECIPIENTS.BCC]);
  }

  const getTemplatePreview = async (campaign, templateContent) => {
    let markup;
    try {
      if (
        getNotification(campaign, notificationIndex)?.[NOTIFICATION.TYPE] ===
        NOTIFICATION_CHANNELS.mail.value
      ) {
        const variableList =
          getTemplateMustacheVariables(templateContent) || {};
        const variables = {};
        Object.keys(variableList)?.forEach((varName) => {
          variables[varName] = `{{${variableList[varName].srcname}}}`;
        });
        // sn_email_snippet is stored in notification mappings, not in the
        // template HTML, so it must be added explicitly. Without this, the
        // backend (strict: false) leaves the raw {{[sn_email_snippet]}} token
        // visible in the preview when the snippet is empty or not set.
        const notification = getNotification(campaign, notificationIndex);
        variables[UI_DATA.SNIPPET_TEXT] = getSnippet(notification) ?? "";
        markup = await previewTemplate(campaign[CAMPAIGN.UID], variables);
      } else {
        throw new Error("Can only get preview markup for email notifications.");
      }
    } catch (err) {
      markup = "";
    }
    if (_isMounted.current) {
      setTemplatePreview(markup);
    }
  };

  // if a csv has already been uploaded for the campaign, fetch it
  const getExistingCsv = async (campaign) => {
    await getCSVFile(campaign);
    await updateDataSourceHeadings();
  };

  const getCSVFile = async (campaign) => {
    let file = null;
    console.log("getExistingCsv:campaign:", campaign);
    if (campaign) {
      file = await getUploadedCSVFileName(
        campaign[CAMPAIGN.TEAM_ID],
        campaign[CAMPAIGN.UID]
      );
    }
    setCsvFile(file);
    return file;
  }

  // if a csv is present, remove the same
  const removeExistingCsvIfPresent = async (campaign) => {
    if (campaign) {
      let removed = await removeUploadedCSVFileName(
        campaign[CAMPAIGN.TEAM_ID],
        campaign[CAMPAIGN.UID]
      );
      if (removed) {
        setCsvFile(null);
        await updateDataSourceHeadings(campaign);
      }
    }
  };

  //V1 - to be remooved
  const updateDataSourceHeadings = async (campaign, resetSettings = false, channelOverride) => {
    let headings = [];
    const updatedCampaign = _.cloneDeep(campaign) || selectedCampaign || {};
    if (updatedCampaign) {
      try {
        let metaData = updatedCampaign[CAMPAIGN.UID]
          ? await getCampaignMetaData(updatedCampaign[CAMPAIGN.UID])
          : [];

        metaData = metaData.map((heading) => heading.trim());
        if (metaData.length) {
          updatedCampaign[CAMPAIGN.METADATA] = {
            ...updatedCampaign?.[CAMPAIGN.METADATA],
            [CAMPAIGN.METADATA_HEADINGS]: metaData,
          };
          metaData.map((heading) =>
            headings.push({
              value: heading.trim(),
              label: heading.trim(),
              variable: makeTemplateFieldName(heading),
              type: "csv",
            })
          );
          
        } else {
          updatedCampaign[CAMPAIGN.METADATA] = {
            ...updatedCampaign?.[CAMPAIGN.METADATA],
            [CAMPAIGN.METADATA_HEADINGS]: [],
          };
        }

        if (
          updatedCampaign?.data_source?.type === "csv" ||
          updatedCampaign?.general_data_source_type === "csv"
        ) {
          validatePersonalizedFields(metaData);
        }

        setSelectedCampaign(updatedCampaign);
        let templateContent = selectedTemplate?.[TEMPLATE.CONTENT];
        if (!templateContent) {
          const template = await getTemplate(
            updatedCampaign[CAMPAIGN.UID],
            channelOverride || channel
          );
          templateContent = template?.[TEMPLATE.CONTENT];
        }
        removeDeprecatedMustacheVariables(templateContent, headings);
        setSelectedCampaign(updatedCampaign);
      } catch (err) {
        // no meta data exists
      }
    }
    setDataSourceHeadings(headings);
    if (resetSettings) {
      // reset configs that use data headings
      let notification = getNotification(updatedCampaign, notificationIndex);
      if (notification) {
        updatedCampaign[CAMPAIGN.NOTIFICATIONS][notificationIndex][
          NOTIFICATION.RECIPIENTS
        ] = [];
      }
    }
    setSelectedCampaign(updatedCampaign);
    return headings;
  };

  //V2 updateDataSourceHeadings
  const updateDataSourceHeadingsV2 = async (campaign, resetSettings = false) => {
    let dataSourceHeadings = {};
    const updatedCampaign = _.cloneDeep(campaign) || selectedCampaign || {};
    if (updatedCampaign) {
      try {
        let metaData = updatedCampaign[CAMPAIGN.UID]
          ? await getCampaignMetaDataV2(updatedCampaign[CAMPAIGN.UID])
          : {};
          console.log({metaData});
          
        if (!_.isEmpty(metaData)) {
          updatedCampaign[CAMPAIGN.METADATA] = {
            ...updatedCampaign?.[CAMPAIGN.METADATA],
            [CAMPAIGN.METADATA_HEADINGS]: metaData,
          };

          for(const fileName in metaData) {
            console.log(fileName);
            const headings = metaData[fileName];

            dataSourceHeadings[fileName] = headings.map((heading) => ({
              value: heading.trim(),
              label: heading.trim(),
              variable: makeTemplateFieldName(heading),
              type: "csv",
            }))
          }
          console.log({dataSourceHeadings});
        } else {
          updatedCampaign[CAMPAIGN.METADATA] = {
            ...updatedCampaign?.[CAMPAIGN.METADATA],
            [CAMPAIGN.METADATA_HEADINGS]: {},
          };
        }

        // metaData = metaData.map((heading) => heading.trim());
        if (updatedCampaign?.data_source?.campaign_type === "advanced") {
          if(Object.keys(metaData).length !== 1) {
            //Advanced campaign should only have one CSV file uploaded
            setSnackMessage("Advanced campaign does not support multiple CSV Files");
            setSnackType(SNACK_TYPES.ERROR)
            // return;
          }

          const fileName = Object.keys(metaData)[0]; //Advanced campaign only have one CSV File uploaded
          const headings = metaData[fileName];
          validatePersonalizedFields(headings);

          let templateContent = selectedTemplate?.[TEMPLATE.CONTENT];

          if (!templateContent) {
            const template = await getTemplate(
              updatedCampaign[CAMPAIGN.UID],
              channel
            );
            templateContent = template?.[TEMPLATE.CONTENT];
          }
          // removeDeprecatedMustacheVariables(templateContent, dataSourceHeadings[fileName]);
        }
      }  catch (err) {
        // no meta data exists
        console.error({err});
        
      }
    }
    setDataSourceHeadingsV2(dataSourceHeadings);
    if (resetSettings) {
      // reset configs that use data headings
      let notification = getNotification(updatedCampaign, notificationIndex);
      if (notification) {
        updatedCampaign[CAMPAIGN.NOTIFICATIONS][notificationIndex][
          NOTIFICATION.RECIPIENTS
        ] = [];
      }
    }
    console.log({updatedCampaign});
    
    setSelectedCampaign({...updatedCampaign});
    return {
      dataSourceHeadings,
      updatedCampaign,
    };
  };


  //Only "Admin", "Team Owner", "Folder Owner", "General folder Creator", "Private folder Creator with Folder Edit access" can save/update a campaign
  const editCampaignPermissionCheck = useCallback(async (campaign) => {
    const teamId = campaign?.[CAMPAIGN.TEAM_ID];
    console.log({teamUsers});

    let teamMembers = teamUsers?.hasOwnProperty(teamId)
          ? teamUsers[teamId]
          : await getMembersOfTeam(teamId);

    let folder = currentFolder;
    console.log({folder});
    
    if(_.isEmpty(folder) || folder[FOLDER.FOLDER_ID] !== campaign?.[CAMPAIGN.FOLDER_ID]) {
      const folderId = campaign?.[CAMPAIGN.FOLDER_ID];
      folder = await getFolder(folderId);
      setCurrentFolder(folder); // Update the context
    }

    const isFolderOwner = getIsFolderOwner(user, folder[FOLDER.PERMISSIONS]);
    const folderUserRole = getUserRole(user, folder[FOLDER.PERMISSIONS]);
    const signedInUserIsOwner = isSignedInUserATeamOwner(user, teamMembers);
    const folderType = folder[FOLDER.PERMISSION_TYPE];

    return canEditCampagin(user, signedInUserIsOwner, campaign, isFolderOwner, folderUserRole, folderType);
  }, [
    teamUsers,
    getMembersOfTeam,
    currentFolder,
    getFolder,
    setCurrentFolder,
    getIsFolderOwner,
    getUserRole,
    user,
    canEditCampagin
  ]);

  // compare current data source headings with variables in the template
  // if a template variable doesn't have a corresponding csv heading, remove it
  const removeDeprecatedMustacheVariables = async (
    templateContent,
    dataSourceHeadings
  ) => {
    let updatedTemplateContent = removeInvalidDynamicContent(
      templateContent,
      dataSourceHeadings,
      false
    );
    if (updatedTemplateContent) {
      await saveTemplate({
        content: updatedTemplateContent,
        isNewTemplate: false,
        showMessage: false,
      }); // update the template
    }
  };
  // if campaign type is csv, personalized recipient fields are only valid if they
  // match one of the csv metadata headings
  const validatePersonalizedFields = (metadataArray) => {
    const recipients = getRecipientsFieldValues(
      selectedCampaign,
      notificationIndex
    );
    handleRecipientsInput(
      setTo,
      RECIPIENTS.TO,
      validateField(recipients[RECIPIENTS.TO], metadataArray)
    );
    handleRecipientsInput(
      setCc,
      RECIPIENTS.CC,
      validateField(recipients[RECIPIENTS.CC], metadataArray)
    );
    handleRecipientsInput(
      setBcc,
      RECIPIENTS.BCC,
      validateField(recipients[RECIPIENTS.BCC], metadataArray)
    );
  };

  // deselects the campaign, refreshes the campaign list and redirects there
  // also detects the status of the closed campaign to set the campaign status tabs accordingly
  const closeCampaign = async (statusOverride) => {
    if (statusOverride) {
      setStatusTabOverride(statusOverride);
    } else if (selectedCampaign) {
      const statuses = getCampaignStatuses(selectedCampaign);
      if (statuses.includes(CAMPAIGN_STATUSES.SENT.value)) {
        setStatusTabOverride(CAMPAIGN_STATUSES.SENT.value);
      } else if (statuses.includes(CAMPAIGN_STATUSES.SCHEDULED.value)) {
        setStatusTabOverride(CAMPAIGN_STATUSES.SCHEDULED.value);
      } else if (statuses.includes(CAMPAIGN_STATUSES.DRAFT.value)) {
        setStatusTabOverride(CAMPAIGN_STATUSES.DRAFT.value);
      } else {
        setStatusTabOverride(undefined);
      }
    } else {
      setStatusTabOverride(undefined);
    }
    toggleLoadingCampaign(true);
    await fetchCampaigns();
    originalCampaign = {};

    /*
    When navigateTo is available/not undefined, navigate to previous page(home:/) using goBack to enable browser forward option.
    Otherwise, navigate to home page using push, without enabling forward option. This happens when the user lands to the campaigns page directly in a fresh tab/window.
    */
    if (state?.from) {
      history.goBack();
    } else {
      history.replace("/");
    }
  };

  const resetWarningRequired = () => {
    if (
      to?.length ||
      cc?.length ||
      bcc?.length ||
      subject ||
      snippet ||
      from ||
      priority ||
      clickTracking ||
      viewTracking ||
      selectedTemplate
    ) {
      return true;
    }
    return false;
  };

  const resetNotificationConfigurations = () => {
    // reset campaign details page
    handleRecipientsInput(setTo, RECIPIENTS.TO, null);
    handleRecipientsInput(setCc, RECIPIENTS.CC, null);
    handleRecipientsInput(setBcc, RECIPIENTS.BCC, null);
    handleConfigurationInput(setFrom, CONFIGURATIONS.FROM, null);
    handleConfigurationInput(
      setFromDisplayName,
      CONFIGURATIONS.FROM_DISPLAY_NAME,
      null
    );
    handleConfigurationInput(setSubject, CONFIGURATIONS.SUBJECT, null);
    handleMappingUpdate(setSnippet, UI_DATA.SNIPPET_TEXT, null);
    handleConfigurationInput(setPriority, CONFIGURATIONS.PRIORITY, null);
    handleConfigurationInput(setAttachments, CONFIGURATIONS.ATTACHMENTS, null);
    handleConfigurationInput(setHasExternalUsers, EXTERNAL_USER_OPTIONS_CONFIGS.INCLUDE_UNSUBSCRIBE_LINK, null);
  };

  const resetConfigurationsForCampaignTypeChange = () => {
    handleRecipientsInput(setTo, RECIPIENTS.TO, null);
    handleRecipientsInput(setCc, RECIPIENTS.CC, null);
    handleRecipientsInput(setBcc, RECIPIENTS.BCC, null);
    handleConfigurationInput(setHasExternalUsers, EXTERNAL_USER_OPTIONS_CONFIGS.INCLUDE_UNSUBSCRIBE_LINK, null);
  }

  // handles click of channel selection card in the edit flow
  const handleChannelSelect = (newChannel, toggleWarning) => {
    const isReset = resetWarningRequired() || campaignType || csvFile;
    if (toggleWarning && isReset) {
      toggleWarning();
    } else {
      let campaign = selectedCampaign;
      if (campaign) {
        let notification = getNotification(campaign, notificationIndex);
        if (notification) {
          resetNotificationConfigurations(); // clear out existing configs
          notification[NOTIFICATION.TEMPLATE] = null;
          setSelectedTemplate(null);
          setTemplatePreview(null);
          if (newChannel) {
            notification[NOTIFICATION.TYPE] = newChannel;
            delete notification[NOTIFICATION.TEMPLATE];
            campaign[CAMPAIGN.NOTIFICATIONS][notificationIndex] = notification;
          }
        } else if (newChannel) {
          notification = { [NOTIFICATION.TYPE]: newChannel };
          if (campaign[CAMPAIGN.NOTIFICATIONS]) {
            campaign[CAMPAIGN.NOTIFICATIONS].push(notification);
            setNotificationIndex(notificationIndex + 1);
          } else {
            campaign[CAMPAIGN.NOTIFICATIONS] = [notification];
            setNotificationIndex(0);
          }
        }
        if (!newChannel) {
          // if no channel, remove the entire notification from the array
          campaign = removeNotificationAtIndex(campaign, notificationIndex);
        }
        setChannel(newChannel);
        setSelectedCampaign(campaign);
        if (
          newChannel === NOTIFICATION_CHANNELS.teams.value
          || campaign[CAMPAIGN.IS_SYSTEM_NOTIFICATIONS] === true
        ) {
          handleCampaignTypeSelect(
            CAMPAIGN_TYPES.personalized.value,
            false,
            true
          );
        } else {
          handleCampaignTypeSelect(CAMPAIGN_TYPES.general.value, false, true);
        }
      }
      setSteps(getStepperConfig(campaign, newChannel, campaignType));
    }
  };

  /**
   * To update campaign name and channel type
   */
  const updateCampaignNameAndChannel = async (campaignObj) => {
    const newChannel = getChannel(getNotification(campaignObj, notificationIndex));
    const currentChannel = getChannel(getNotification(selectedCampaign, notificationIndex));
    
    let campaign = selectedCampaign;

    campaign[CAMPAIGN.NAME] = campaignObj[CAMPAIGN.NAME];
    let channelUpdated = false;
    if(newChannel !== currentChannel) {      
      campaign[CAMPAIGN.DATA_SOURCE] = _.cloneDeep(campaignObj[CAMPAIGN.DATA_SOURCE]);
      campaign[CAMPAIGN.GENERAL_DATA_SOURCE_TYPE] = campaignObj[CAMPAIGN.GENERAL_DATA_SOURCE_TYPE];
      let notification = getNotification(campaign, notificationIndex);
      if(notification) {
        resetNotificationConfigurations(); // clear out existing configs
        notification[NOTIFICATION.TEMPLATE] = null;
        setSelectedTemplate(null);
        setTemplatePreview(null);
        if (newChannel) {
          notification[NOTIFICATION.TYPE] = newChannel;
          delete notification[NOTIFICATION.TEMPLATE];
          campaign[CAMPAIGN.NOTIFICATIONS][notificationIndex] = notification;
        }
      } else if (newChannel) {
        notification = { [NOTIFICATION.TYPE]: newChannel };
        if (campaign[CAMPAIGN.NOTIFICATIONS]) {
          campaign[CAMPAIGN.NOTIFICATIONS].push(notification);
          setNotificationIndex(notificationIndex + 1);
        } else {
          campaign[CAMPAIGN.NOTIFICATIONS] = [notification];
          setNotificationIndex(0);
        }
      }
      channelUpdated = true;
    }
    const channel = getChannel(getNotification(campaign, notificationIndex));  
    setChannel(channel);
    let campaignType;
    if (channel === NOTIFICATION_CHANNELS.teams.value) {
      campaignType = CAMPAIGN_TYPES.personalized.value;
    } else {
      // campaignType = getCampaignTypeV2(campaign);
      campaignType = getCampaignType(campaign);
    }
    setCampaignType(campaignType);
    const error = await saveCampaign(campaign);
    if(error) {
      return error;
    }
    return {
      campaignType,
      channel,
      channelUpdated,
    };
  }

  // handles click of connection type selection card in the edit flow
  const handleCampaignTypeSelect = async (type, showWarning, isReset) => {
    console.log(`handleCampaignTypeSelect(type=${type}, showWarning=${showWarning}, isReset=${isReset})`);
    isReset = isReset || resetWarningRequired() || csvFile;
    if (showWarning && isReset) {
      showWarning();
    } else {
      let campaign = selectedCampaign;
      // campaign["general_data_source_type"] = null;
      if (campaign) {
        resetConfigurationsForCampaignTypeChange();
        switch (type) {
          case CAMPAIGN_TYPES.personalized.value:
            campaign[CAMPAIGN.DATA_SOURCE] = {
              [DATA_SOURCE.TYPE]:
                CAMPAIGN_TYPES.personalized.dataSourceTypes.CSV.value,
            };
            campaign[CAMPAIGN.GENERAL_DATA_SOURCE_TYPE] = null;
            break;
          case CAMPAIGN_TYPES.general.value:
            campaign[CAMPAIGN.DATA_SOURCE] = {
              [DATA_SOURCE.TYPE]: CAMPAIGN_TYPES.general.dataSourceTypes.MANUAL.value
            };
            campaign[CAMPAIGN.METADATA] = {
              ...campaign?.[CAMPAIGN.METADATA],
              [CAMPAIGN.METADATA_HEADINGS]: [],
            };
            campaign[CAMPAIGN.GENERAL_DATA_SOURCE_TYPE] = CAMPAIGN_TYPES.general.dataSourceTypes.MANUAL.value;
            campaign = removeUnusedMappings(campaign, []);
            break;
          case undefined:
            campaign[CAMPAIGN.DATA_SOURCE] = {};
            campaign[CAMPAIGN.METADATA] = {
              ...campaign?.[CAMPAIGN.METADATA],
              [CAMPAIGN.METADATA_HEADINGS]: [],
            };
            campaign = removeUnusedMappings(campaign, []);
            break;
          default:
            break;
        }
        setSelectedCampaign(campaign);
        setCampaignType(type);
        removeExistingCsvIfPresent(selectedCampaign);
      }
      // Save campaign changes
      await saveCampaign(campaign);
    }
  };

  const handleRecipientsInput = (
    recipientHandler,
    recipientType,
    value,
    error
  ) => {
    recipientHandler(value); // update the to, cc, or bcc useState hook
    setValue(recipientType, value); // update the form state with to, cc, or bcc useState hook
    const updatedCampaign = updateRecipientField(
      // now update the campaign object
      selectedCampaign,
      notificationIndex,
      recipientType,
      value,
      csvFile
    );
    if (updatedCampaign) {
      // Do not update the previous reference yet for mutiple recipients at value change
      updatedCampaign.updatePrevRef = false;
    }
    setSelectedCampaign(updatedCampaign);
    updateFieldErrors(recipientType, error, fieldErrors, setFieldErrors);
  };

  const handleRecipientsInputV2 = (
    recipientType,
    value,
    error,
    dataSourceType,
  ) => {    
    const updatedCampaign = updateRecipientFieldV2(
      // now update the campaign object
      selectedCampaign,
      notificationIndex,
      recipientType,
      value,
      dataSourceType
    );
    if (updatedCampaign) {
      // Do not update the previous reference yet for mutiple recipients at value change
      updatedCampaign.updatePrevRef = false;
    }
    setSelectedCampaign(updatedCampaign);
    const updatedRecipientFields = getRecipientFields(updatedCampaign, notificationIndex, recipientType);
    setValue(recipientType, updatedRecipientFields); // update the form state with to, cc, or bcc useState hook
    // recipientHandler(updatedRecipientFields); // update the to, cc, or bcc useState hook
    
    updateFieldErrors(recipientType, error, fieldErrors, setFieldErrors);
  };

  const handleConfigurationInput = (
    configHandler,
    configName,
    value,
    error
  ) => {
    configHandler(value); // update from, subject, snippet, etc
    const updatedCampaign = updateNotificationConfig(
      // now update the campaign object
      selectedCampaign,
      notificationIndex,
      configName,
      value
    );
    
    setSelectedCampaign(_.cloneDeep(updatedCampaign));
    updateFieldErrors(configName, error, fieldErrors, setFieldErrors);
    return updatedCampaign;
  };

  const handleTrackingConfigInput = (trackingType, value) => {
    console.log("handleTrackingConfigInput", trackingType, value);
    trackingMetricSetter[trackingType](value);

    // set trackView/trackClick
    let updatedCampaign = updateNotificationConfig(
      selectedCampaign,
      notificationIndex,
      trackingType,
      value ? String(value) : null
    );

    // set trackViewCampaignId/trackClickCampaignId
    if (Object.values(TRACKING_CONFIGS).includes(`${trackingType}CampaignId`))
      updatedCampaign = updateNotificationConfig(
        updatedCampaign,
        notificationIndex,
        `${trackingType}CampaignId`,
        value ? selectedCampaign[CAMPAIGN.UID] : null
      );

    if (
      (trackingType === TRACKING_CONFIGS.VIEW ||
        trackingType === TRACKING_CONFIGS.CLICK) &&
      !value
    ) {
      CAMPAIGN_FIELDS.TRACKING.forEach(
        ({ name, enableIfTracked, enableRef }) => {
          if (enableIfTracked && trackingType === enableRef) {
            updatedCampaign = updateNotificationConfig(
              updatedCampaign,
              notificationIndex,
              name,
              null
            );
            trackingMetricSetter[name] && trackingMetricSetter[name](false);
          }
        }
      );
    }
    setSelectedCampaign(updatedCampaign);
  };

  const handleExternalUsersOptions = (configType, value) =>{
    const isDetailStepComplete = (steps, selectedCampaign) => {
      const detailStep = steps.find(step => step.label === "Details");
      return detailStep && detailStep.isComplete(selectedCampaign,false, 0, isToggleEnabled(FEATURE.EXTERNAL_USERS_CHECK))
    };
    if (isDetailStepComplete(steps, selectedCampaign)) {
      toggleIsExecutable(false);
    }

    let updatedCampaign = updateNotificationConfig(
      selectedCampaign,
      notificationIndex,
      configType,
      value ? String(value) : null
    );

    setSelectedCampaign(updatedCampaign);
  }

  const handleSaveOnChangeOrBlur = async (fieldName, previousValue, newValue) => {
    // Normalize values to handle empty strings and nulls
    // Skip save when both values are empty or there is no change
    const isEmptyValue = (val) => val === null || val === undefined || val === '' || val === ' ' || (Array.isArray(val) && val.length === 0);
    if (
      (isEmptyValue(previousValue) && isEmptyValue(newValue)) ||
      (previousValue === newValue)
    ) {
      return;
    }

    const normalizedPreviousValue = previousValue ?? "";
    const normalizedNewValue = newValue ?? "";
    let valuesAreDifferent = false;

    if (Array.isArray(normalizedPreviousValue) && Array.isArray(normalizedNewValue)) {
      if (normalizedPreviousValue.length !== normalizedNewValue.length) {
        valuesAreDifferent = true;
      } else {
        // Compare arrays
        const areEqual = normalizedPreviousValue.every((item, index) => {
          if (typeof item !== 'object' || item === null) {
            return item === normalizedNewValue[index];
          }
          // For simple objects, compare stringified versions of just that item
          return JSON.stringify(item) === JSON.stringify(normalizedNewValue[index]);
        });
        valuesAreDifferent = !areEqual;
      }
    } else {
      valuesAreDifferent = normalizedPreviousValue !== normalizedNewValue;
    }
    if (valuesAreDifferent) {
      await saveCampaign(selectedCampaign);
      // After saving the campaign, update the previous ref state
      const updatedCampaign = {
        ...selectedCampaign,
        updatePrevRef: true
      }
      setSelectedCampaign(updatedCampaign); 
    }
  }

  const handleCampaignNameAndCategory = async (name, categoryId) => {
    const campaign = {
      ...selectedCampaign,
      [CAMPAIGN.NAME]: name.trim(),
      [CAMPAIGN.CATEGORY_ID]: Number(categoryId),
    };
    return await saveCampaign(campaign, false);
  };

  const handleCampaignLastSavedTime = (campaign, lastSaved="") => {
    try {
      let lastSavedTime = "";
      if (campaign) {
        if (lastSaved) {
          lastSavedTime = lastSaved;
        } else {
          lastSavedTime = campaign[CAMPAIGN.DATE_UPDATED] ? campaign[CAMPAIGN.DATE_UPDATED] : campaign[CAMPAIGN.DATE_CREATED];
        }
        lastSavedTime = formatDate(lastSavedTime, true, true, true);
        setCampaignLastSavedTime(lastSavedTime);
      }
    } catch (error) {
      console.error("Error in handleCampaignLastSavedTime:", error);
    }
  };

  const handleMappingUpdate = (mappingHandler, templateName, value, error) => {
    mappingHandler(value); // update the context state
    const updatedCampaign = updateMapping(
      selectedCampaign,
      notificationIndex,
      templateName,
      value,
      MAPPING.CONSTANT
    );
    setSelectedCampaign(updatedCampaign);
    updateFieldErrors(templateName, error, fieldErrors, setFieldErrors);
  };

  // determine if a campaign has been edited and needs to be saved before closing
  const checkIfEdited = () => {
    if (selectedCampaign) {
      const newCampaign = _.cloneDeep(selectedCampaign);
      if (newCampaign?.[CAMPAIGN.NOTIFICATIONS]?.length) {
        const nonEmptyNotifications = [];
        newCampaign[CAMPAIGN.NOTIFICATIONS].forEach((notification, index) => {
          if (Object.keys(notification).length) {
            const newPriority = getPriority(notification);
            // remove default priority value from consideration (unless an original priority value is set too)
            if (
              newPriority &&
              newPriority === PRIORITIES.medium.value &&
              !getPriority(originalCampaign?.[CAMPAIGN.NOTIFICATIONS]?.[index])
            ) {
              const priorityIndex = getConfigIndex(
                notification[NOTIFICATION.CONFIGURATIONS],
                CONFIGURATIONS.NAME,
                CONFIGURATIONS.PRIORITY
              );
              notification[NOTIFICATION.CONFIGURATIONS].splice(
                priorityIndex,
                1
              );
            }
            nonEmptyNotifications.push(notification);
          }
        });
        if (nonEmptyNotifications.length) {
          newCampaign[CAMPAIGN.NOTIFICATIONS] = nonEmptyNotifications;
        } else {
          delete newCampaign[CAMPAIGN.NOTIFICATIONS];
        }
      }
      delete originalCampaign?.[CAMPAIGN.METADATA];
      delete newCampaign?.[CAMPAIGN.METADATA];
      return JSON.stringify(originalCampaign) !== JSON.stringify(newCampaign);
    } else {
      return false;
    }
  };

  const duplicateCampaign = async (campaign, name) => {
    setCampaignError(null);
    const teamId = selectedTeam[TEAM.ID];
    const userUpn = user[JWT.UPN];
    if (name && teamId && userUpn) {
      toggleLoadingCampaign(true);
      try {
        let campaignToDuplicate = await getCampaign(campaign?.[CAMPAIGN.UID]);
        // remove uid, metadata, execution info
        delete campaignToDuplicate[CAMPAIGN.UID];
        delete campaignToDuplicate[CAMPAIGN.METADATA];
        delete campaignToDuplicate[CAMPAIGN.DATE_LAST_EXECUTED];
        delete campaignToDuplicate[CAMPAIGN.LAST_EXECUTED_BY];
        delete campaignToDuplicate[CAMPAIGN.DATE_UPDATED];
        // remove tracking configs from all notifications
        let index = 0;
        while (index < campaignToDuplicate[CAMPAIGN.NOTIFICATIONS]?.length) {
          if (
            getCampaignType(campaignToDuplicate) ===
            CAMPAIGN_TYPES.personalized.value
          ) {
            // remove the recipients fields and the auto cip mappings
            campaignToDuplicate = resetRecipients(campaignToDuplicate, index);
          }
          if (!(isUserSuperAdmin(user) || isUserAdmin(user))) {
            // remove the from field if user role is member
            campaignToDuplicate = updateNotificationConfig(
              campaignToDuplicate,
              index,
              CONFIGURATIONS.FROM,
              null
            );
          }
          campaignToDuplicate = removeUnusedMappings(campaignToDuplicate, []); // remove all csv mappings from the campaign
          campaignToDuplicate = updateNotificationConfig(
            campaignToDuplicate,
            index,
            CONFIGURATIONS.ATTACHMENTS,
            null
          );
          //reset all tracking configs
          for (const config in TRACKING_CONFIGS) {
            campaignToDuplicate = updateNotificationConfig(
              campaignToDuplicate,
              index,
              TRACKING_CONFIGS[config],
              null
            );
          }
          index = index + 1;
        }
        campaignToDuplicate = updateCronTrigger(campaignToDuplicate, null);
        // create a new campaign
        const response = await postCampaign({
          ...campaignToDuplicate,
          [CAMPAIGN.NAME]: name.trim(),
          [CAMPAIGN.FOLDER_ID]: folder_id,
          [CAMPAIGN.IS_DRAFT]: true,
          [CAMPAIGN.TEAM_ID]: teamId,
          [CAMPAIGN.OWNER]: userUpn,
        });
        const createdId = response.campaignId;
        if (_isMounted.current) {
          history.push({
            pathname: `/${ROUTES.CAMPAIGN}/${createdId}/edit`,
          });
          setSnackMessage(SUCCESS_MSG.DUPLICATE_CAMPAIGN);
          setSnackType(SNACK_TYPES.SUCCESS);
        }
      } catch (error) {
        let message = ERROR_MSG.POST_CAMPAIGN;
        if (error.response?.data?.code === CODES.CAMPAIGN_NAME_TAKEN) {
          message = ERROR_MSG.CAMPAIGN_NAME_TAKEN;
        }
        setCampaignError(message);
        trackEvent(EVENTS.ERRORS.GENERIC_ERROR, {errorMsg: message, pageTitle: pageTitle}, EVENT_TYPES.CREATE_MESSAGE_ERRORS.DUPLICATE_CAMPAIGN_ERROR)
        return error;
      }
    }
  };

  // handles saving the current campaign between steps
  const goToStep = async (stepIndex) => {
    let saveError;
    if (checkIfEdited()) {
      saveError = await saveCampaign(selectedCampaign, false);
    }
    if (!saveError) {
      setCurrentStep(stepIndex);
    }
  };

  const saveAsTemplate = async (templateName, templateContent) => {
    try {
      const teamId = selectedTeam[TEAM.ID];
      const userUpn = user[JWT.UPN];
      if (templateName && teamId && userUpn) {
        await postTeamTemplate(teamId, {
          [TEMPLATE.NAME]: templateName,
          [TEMPLATE.TYPE]: NOTIFICATION_CHANNELS.mail.value,
          [TEMPLATE.CONTENT]: templateContent,
          [TEMPLATE.OWNER]: userUpn,
        });
        setSnackMessage(SUCCESS_MSG.PUT_SAVED_TEMPLATE);
        setSnackType(SNACK_TYPES.SUCCESS);
      }
    } catch (error) {
      setCampaignError(ERROR_MSG.POST_SAVED_TEMPLATE);
    }
  };

  const downloadCampaignCSV = async (campaign) => {
    try {
      if (campaign) {
        toggleIsDownloading(true);
        await downloadCSV(campaign[CAMPAIGN.TEAM_ID], campaign[CAMPAIGN.UID]);
        toggleIsDownloading(false);
        setSnackMessage("Download Completed");
        setSnackType("success");
        setDownloadError(false);
      }
    } catch (err) {
      setDownloadError(true);
    }
  };

  const setPrevCampaignRefCurrent = (prevCampaignRef, configs, fieldName, currVal) => {
    if (prevCampaignRef.current) {
      // Update the prevCampaignRef with current values before handleChange is completed
      const notification = prevCampaignRef.current?.notifications?.[notificationIndex];
      if (notification) {
        let currentCampaign;
        if (configs == NOTIFICATION.RECIPIENTS) {
          currentCampaign = updateRecipientField(prevCampaignRef.current, notificationIndex, fieldName, currVal, csvFile);
        } else if (configs == NOTIFICATION.CONFIGURATIONS) {
          currentCampaign = updateNotificationConfig(prevCampaignRef.current, notificationIndex, fieldName, currVal);
        } else if (configs == NOTIFICATION.MAPPING) {
          currentCampaign = updateMapping(prevCampaignRef.current, notificationIndex, fieldName, currVal);
        } else if (configs == NOTIFICATION.TRACKING) {
          const value = currVal ? String(currVal) : null;
          currentCampaign = updateNotificationConfig(prevCampaignRef.current, notificationIndex, fieldName, value);
        }
        prevCampaignRef.current = currentCampaign;
      }
    }
    return prevCampaignRef;
  }

  const notificationRecipientsFields = {
    [RECIPIENTS.TO]: {
      value: to,
      handleChange: (value, error) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.RECIPIENTS, RECIPIENTS.TO, to)
        handleRecipientsInput(setTo, RECIPIENTS.TO, value, error);
      },
      handleSaveOnChangeOrBlur: (fieldName, newValue, prevValue) => {
        let prevTo = getRecipientsFieldValues(
          prevCampaignRef.current, notificationIndex, RECIPIENTS.TO
        )
        handleSaveOnChangeOrBlur(fieldName, prevTo, newValue);
      },
    },
    [RECIPIENTS.CC]: {
      value: cc,
      handleChange: (value, error) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.RECIPIENTS, RECIPIENTS.CC, cc)
        handleRecipientsInput(setCc, RECIPIENTS.CC, value, error);
      },
      handleSaveOnChangeOrBlur: (fieldName, newValue, prevValue) => {
        let prevCc = getRecipientsFieldValues(
          prevCampaignRef.current, notificationIndex, RECIPIENTS.CC
        )
        handleSaveOnChangeOrBlur(fieldName, prevCc, newValue);
      },
    },
    [RECIPIENTS.BCC]: {
      value: bcc,
      handleChange: (value, error) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.RECIPIENTS, RECIPIENTS.BCC, bcc)
        handleRecipientsInput(setBcc, RECIPIENTS.BCC, value, error);
      },
      handleSaveOnChangeOrBlur: (fieldName, newValue, prevValue) => {
        let prevBcc = getRecipientsFieldValues(
          prevCampaignRef.current, notificationIndex, RECIPIENTS.BCC
        )
        handleSaveOnChangeOrBlur(fieldName, prevBcc, newValue);
      },
    },
  }

    const notificationRecipientsFieldsV2 = {
    [RECIPIENTS.TO]: {
      value: to,
      handleChange: (value, dataSourceType, error) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.RECIPIENTS, RECIPIENTS.TO, to)
        handleRecipientsInputV2(RECIPIENTS.TO, value, error, dataSourceType);
      },
      handleSaveOnChangeOrBlur: (fieldName, newValue, prevValue) => {
        let prevTo = getRecipientsFieldValues(
          prevCampaignRef.current, notificationIndex, RECIPIENTS.TO
        )
        handleSaveOnChangeOrBlur(fieldName, prevTo, newValue);
      },
    },
    [RECIPIENTS.CC]: {
      value: cc,
      handleChange: (value, dataSourceType, error) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.RECIPIENTS, RECIPIENTS.CC, cc)
        handleRecipientsInputV2(RECIPIENTS.CC, value, error, dataSourceType);
      },
      handleSaveOnChangeOrBlur: (fieldName, newValue, prevValue) => {
        let prevCc = getRecipientsFieldValues(
          prevCampaignRef.current, notificationIndex, RECIPIENTS.CC
        )
        handleSaveOnChangeOrBlur(fieldName, prevCc, newValue);
      },
    },
    [RECIPIENTS.BCC]: {
      value: bcc,
      handleChange: (value, dataSourceType, error) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.RECIPIENTS, RECIPIENTS.BCC, bcc)
        handleRecipientsInputV2(RECIPIENTS.BCC, value, error, dataSourceType);
      },
      handleSaveOnChangeOrBlur: (fieldName, newValue, prevValue) => {
        let prevBcc = getRecipientsFieldValues(
          prevCampaignRef.current, notificationIndex, RECIPIENTS.BCC
        )
        handleSaveOnChangeOrBlur(fieldName, prevBcc, newValue);
      },
    },
  }

  const notificationConfigurationFields = {
    [CONFIGURATIONS.FROM]: {
      value: from,
      handleChange: (value) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.CONFIGURATIONS, CONFIGURATIONS.FROM, from)
        handleConfigurationInput(setFrom, CONFIGURATIONS.FROM, value);
      },
      handleSaveOnChangeOrBlur: (fieldName, newValue) => {
        const prevNotification = prevCampaignRef.current?.notifications?.[notificationIndex] || [];
        const prevFrom = getFromField(prevNotification) ?? null;
        handleSaveOnChangeOrBlur(fieldName, prevFrom, newValue);
      },
    },
    [CONFIGURATIONS.FROM_DISPLAY_NAME]: {
      value: fromDisplayName,
      handleChange: (value, error) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.CONFIGURATIONS, CONFIGURATIONS.FROM_DISPLAY_NAME, fromDisplayName)
        handleConfigurationInput(
          setFromDisplayName,
          CONFIGURATIONS.FROM_DISPLAY_NAME,
          value,
          error
        );
      },
      handleSaveOnChangeOrBlur: (fieldName, newValue) => {
        const prevNotification = prevCampaignRef.current?.notifications?.[notificationIndex] || [];
        const prevFromDisplayName = getFromDisplayNameField(prevNotification) ?? null;
        handleSaveOnChangeOrBlur(fieldName, prevFromDisplayName, newValue);
      },
      toggleDisabled: !isToggleEnabled(FEATURE.USE_FROM_DISPLAY_NAME),
    },
    [CONFIGURATIONS.SUBJECT]: {
      value: subject,
      handleChange: (value, error) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.CONFIGURATIONS, CONFIGURATIONS.SUBJECT, subject)
        handleConfigurationInput(
          setSubject,
          CONFIGURATIONS.SUBJECT,
          value,
          error
        );
      },
      handleSaveOnChangeOrBlur: (fieldName, newValue) => {
        const prevNotification = prevCampaignRef.current?.notifications?.[notificationIndex] || [];
        const prevSubject = getSubject(prevNotification) ?? null;
        handleSaveOnChangeOrBlur(fieldName, prevSubject, newValue);
      },
    },
    [CONFIGURATIONS.PRIORITY]: {
      value: priority,
      handleChange: (value) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.CONFIGURATIONS, CONFIGURATIONS.PRIORITY, priority)
        handleConfigurationInput(setPriority, CONFIGURATIONS.PRIORITY, value);
      },
      handleSaveOnChangeOrBlur: (fieldName, newValue) => {
        const prevNotification = prevCampaignRef.current?.notifications?.[notificationIndex] || [];
        const prevPriority = getPriority(prevNotification) ?? null;
        handleSaveOnChangeOrBlur(fieldName, prevPriority, newValue);
      },
    },
    [UI_DATA.SNIPPET_TEXT]: {
      value: snippet,
      handleChange: (value, error) => {
        setPrevCampaignRefCurrent(prevCampaignRef, NOTIFICATION.MAPPING, UI_DATA.SNIPPET_TEXT, snippet)
        handleMappingUpdate(setSnippet, UI_DATA.SNIPPET_TEXT, value, error);
    },
      handleSaveOnChangeOrBlur: (fieldName, newValue) => {
        const prevNotification = prevCampaignRef.current?.notifications?.[notificationIndex] || [];
        const prevSnippet = getSnippet(prevNotification) ?? null;
        handleSaveOnChangeOrBlur(fieldName, prevSnippet, newValue);
      },
    },
    [CONFIGURATIONS.ATTACHMENTS]: {
      value: attachments,
      handleChange: (value, error) => {
        return handleConfigurationInput(
          setAttachments,
          CONFIGURATIONS.ATTACHMENTS,
          value,
          error
        );
      }
    },
    [CONFIGURATIONS.INCLUDE_UNSUBSCRIBE_LINK]: {
      value: hasExternalUsers,
      handleChange: (value) => {
        setHasExternalUsers(value);
        handleExternalUsersOptions(EXTERNAL_USER_OPTIONS_CONFIGS.INCLUDE_UNSUBSCRIBE_LINK, value);
      }
    }
  }

const notificationTrackingFields = {
    [TRACKING_CONFIGS.VIEW]: {
      value: viewTracking,
      handleChange: (value) => {
        handleTrackingConfigInput(TRACKING_CONFIGS.VIEW, value);
      },
    },
    [TRACKING_CONFIGS.CLICK]: {
      value: clickTracking,
      handleChange: (value) => {
        handleTrackingConfigInput(TRACKING_CONFIGS.CLICK, value);
      },
    },
    [TRACKING_CONFIGS.ADVANCED_METRICS]: {
      value: advancedMetrics,
      handleChange: (value) => {
        handleTrackingConfigInput(TRACKING_CONFIGS.ADVANCED_METRICS, value);
      },
    },
    [TRACKING_CONFIGS.TRACK_LOCATION]: {
      value: trackLocation,
      handleChange: (value) => {
        handleTrackingConfigInput(TRACKING_CONFIGS.TRACK_LOCATION, value);
      },
    },
  }

  const externalUsersOptionsFields = {
    [EXTERNAL_USER_OPTIONS_CONFIGS.INCLUDE_UNSUBSCRIBE_LINK]: {
      value: hasExternalUsers,
      options:[{label: "Yes", value: "true"}, {label: "No", value: "false"}],
      handleChange: (event) => {
        setHasExternalUsers(event.target.value)
        handleExternalUsersOptions(EXTERNAL_USER_OPTIONS_CONFIGS.INCLUDE_UNSUBSCRIBE_LINK, event.target.value)
      }
    }
  }

  // create one state object to pass to the Provider
  const initialState = {
    userCanEdit,
    channelAllowed,
    loadingCampaign,
    campaignError,
    setCampaignError,
    selectedCampaign,
    setSelectedCampaign,
    setNotificationIndex,
    notificationIndex,
    selectCampaign,
    handleChannelSelect,
    channel,
    setChannel,
    setSubject,
    campaignType,
    setCampaignType,
    csvFile,
    setCsvFile,
    dataSourceHeadings,
    updateDataSourceHeadings,
    handleCampaignTypeSelect,
    steps,
    closeCampaign,
    createCampaign,
    notificationRecipientsFields,
    notificationConfigurationFields,
    notificationTrackingFields,
    externalUsersOptionsFields,
    setFromDisplayName,
    selectedTemplate,
    setSelectedTemplate,
    templatePreview,
    duplicateCampaign,
    saveCampaign,
    saveTemplate,
    sendCampaign,
    sendCampaignTest,
    scheduleCampaign,
    cancelCampaignSchedule,
    checkIfEdited,
    isExecutable,
    currentStep,
    setCurrentStep,
    goToStep,
    handleCampaignNameAndCategory,
    subject,
    clickTracking,
    viewTracking,
    fieldErrors,
    saveAsTemplate,
    calculateCampaignAttachmentSize: () => {
      return getCampaignAttachmentsSize(
        selectedCampaign?.[CAMPAIGN.TEAM_ID],
        selectedCampaign?.[CAMPAIGN.UID]
      );
    },
    downloadCampaignCSV,
    downloadError,
    setAttachments,
    isDownloading,
    savingCampaign,
    toggleSavingCampaign,
    removeExistingCsvIfPresent,
    needToSaveTemplate,
    setNeedToSaveTemplate,
    updateVariableMapping,
    handleExternalUsersOptions,
    campaignExecutions,
    loadCampaignExecutions,
    campaignLastSavedTime,
    handleCampaignLastSavedTime,
    setPrevCampaignRefCurrent,
    createCampaignV2,
    updateCampaignNameAndChannel,
    campaignLoaded,
    formMethods,
    formValues,
    stepValidationStatus,
    setStepValidationStatus,
    campaignEditSteps,
    setCampaignEditSteps,
    scheduleCampaignV2,
    cancelCampaignScheduleV2,
    updateDataSourceHeadingsV2,
    dataSourceHeadingsV2,
    notificationRecipientsFieldsV2,
    getRecipientConfigStatesFromCampaign,
    setRecipientConfigToStates,
    setCampaignLoaded,
    debouncedCampaignSaveRef,
  };
  return <Context.Provider value={initialState}>{children}</Context.Provider>;
}

export default Campaign;
